"use client";

import type React from "react";
import { useState, useEffect } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  VStack,
  FormErrorMessage,
  useToast,
} from "@chakra-ui/react";
import { HiOutlinePhoneMissedCall } from "react-icons/hi";
import { useAppTheme } from "../../../ThemeContext.tsx";

interface EndCallModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (functionConfig: any) => void;
  initialValues?: any;
}

export const EndCallModal: React.FC<EndCallModalProps> = ({
  isOpen,
  onClose,
  onSave,
  initialValues,
}) => {
  const [name, setName] = useState("end_call");
  const [description, setDescription] = useState("");
  const { resolvedTheme } = useAppTheme();
  const isDark = resolvedTheme === "dark";
  const [nameError, setNameError] = useState("");
  const toast = useToast();

  useEffect(() => {
    if (initialValues) {
      setName(initialValues.name || "end_call");
      setDescription(initialValues.description || "");
    }
  }, [initialValues]);

  const validateToolName = (name: string) => {
    if (!name.trim()) {
      setNameError("Tool name cannot be empty");
      return false;
    }

    if (name.length > 64) {
      setNameError("Tool name must be 64 characters or less");
      return false;
    }

    const validNameRegex = /^[a-zA-Z0-9_-]+$/;
    if (!validNameRegex.test(name)) {
      setNameError(
        "Tool name must only contain letters, numbers, underscores, and dashes (no spaces)"
      );
      return false;
    }

    setNameError("");
    return true;
  };

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setName(e.target.value);
    setNameError("");
  };

  const handleSave = () => {
    if (!validateToolName(name)) {
      toast({
        title: "Invalid tool name",
        description: nameError,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    onSave({
      type: "end_call",
      name,
      description,
    });
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <ModalOverlay />
      <ModalContent
        bg={isDark ? "gray.800" : "white"}
        textColor={isDark ? "white" : "gray.800"}
      >
        <ModalHeader style={{ display: "flex", alignItems: "center" }}>
          <HiOutlinePhoneMissedCall size={20} style={{ marginRight: "8px" }} />
          <span>End Call</span>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={4}>
            <FormControl isRequired isInvalid={!!nameError}>
              <FormLabel color={isDark ? "gray.300" : "gray.700"}>
                Name
              </FormLabel>
              <Input
                id="end-call-function-name"
                value={name}
                placeholder="Enter function Name"
                _placeholder={{ color: isDark ? "gray.400" : "gray.600" }}
                onChange={handleNameChange}
                onBlur={() => validateToolName(name)}
                bg={isDark ? "gray.700" : "white"}
                color={isDark ? "white" : "gray.800"}
                borderColor={isDark ? "gray.600" : "gray.300"}
                _hover={{ borderColor: isDark ? "gray.500" : "gray.400" }}
                variant="outline"
                _focus={{
                  borderColor: isDark ? "gray.100" : "gray.800",
                }}
              />
              {nameError && <FormErrorMessage>{nameError}</FormErrorMessage>}
            </FormControl>
            <FormControl>
              <FormLabel color={isDark ? "gray.300" : "gray.700"}>
                Description (Optional)
              </FormLabel>
              <Textarea
                sx={
                  isDark
                    ? {
                        "&::-webkit-scrollbar": {
                          height: "8px",
                        },
                        "&::-webkit-scrollbar-track": {
                          backgroundColor: "#2D3748", // Dark track color
                        },
                        "&::-webkit-scrollbar-thumb": {
                          backgroundColor: "#4A5568", // Dark thumb color
                          borderRadius: "2px",
                        },
                        // For Firefox
                        scrollbarColor: "#4A5568 #2D3748", // thumb, then track
                        _hover: {
                          scrollbarColor: "#4A5568 #2D3748", // same colors on hover
                        },
                      }
                    : {
                        "&::-webkit-scrollbar": {
                          height: "4px",
                        },
                        "&::-webkit-scrollbar-button": {
                          display: "none",
                        },
                        "&::-webkit-scrollbar-track": {
                          backgroundColor: "#fff",
                        },
                        "&::-webkit-scrollbar-thumb": {
                          backgroundColor: "#fff",
                          borderRadius: "2px",
                        },
                        scrollbarColor: "rgb(220, 220, 220) white",
                        _hover: {
                          scrollbarColor: "rgb(220, 220, 220) white",
                        },
                      }
                }
                id="end-call-function-description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Enter function description"
                _placeholder={{ color: isDark ? "gray.400" : "gray.600" }}
                textColor={isDark ? "white" : "gray.800"}
                bg={isDark ? "gray.700" : "white"}
                color={isDark ? "white" : "gray.800"}
                borderColor={isDark ? "gray.600" : "gray.300"}
                _hover={{ borderColor: isDark ? "gray.500" : "gray.400" }}
                variant="outline"
                rows={3}
                _focus={{
                  borderColor: isDark ? "gray.100" : "gray.800",
                }}
              />
            </FormControl>
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Button
            variant="ghost"
            mr={3}
            onClick={onClose}
            colorScheme={isDark ? "black" : "white"}
            textColor={isDark ? "whiteAlpha" : "gray.800"}
          >
            Cancel
          </Button>
          <Button
            colorScheme="gray"
            onClick={handleSave}
            _hover={{ bg: isDark ? "gray.600" : "gray.300" }}
            background={isDark ? "gray.700" : "gray.200"}
            textColor={isDark ? "whiteAlpha" : "gray.800"}
          >
            Save
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
